export const PORTAL_PASSWORD_RESET_PATHS = ['/admin', '/dj-portal', '/leverantor'] as const;

export type PortalPasswordResetPath = (typeof PORTAL_PASSWORD_RESET_PATHS)[number];

export const PASSWORD_RESET_MIN_LENGTH = 8;

export const PASSWORD_RESET_SENT_MESSAGE =
  'Om kontot finns skickar vi en länk för att återställa lösenordet. Kolla även skräpposten.';

type LocationParts = {
  pathname: string;
  search: string;
  hash: string;
};

type AuthError = { message: string } | null;

export type PasswordResetAuthClient = {
  auth: {
    resetPasswordForEmail: (
      email: string,
      options?: { redirectTo?: string },
    ) => Promise<{ error: AuthError }>;
    updateUser: (attrs: { password: string }) => Promise<{ error: AuthError }>;
  };
};

const PASSWORD_RECOVERY_STORAGE_KEY = 'portal_password_recovery';

let passwordRecoveryPending = false;

function persistPasswordRecoveryPending(pending: boolean): void {
  passwordRecoveryPending = pending;
  if (typeof sessionStorage === 'undefined') return;
  try {
    if (pending) sessionStorage.setItem(PASSWORD_RECOVERY_STORAGE_KEY, '1');
    else sessionStorage.removeItem(PASSWORD_RECOVERY_STORAGE_KEY);
  } catch {
    // ignore
  }
}

function readPersistedPasswordRecovery(): boolean {
  if (typeof sessionStorage === 'undefined') return false;
  try {
    return sessionStorage.getItem(PASSWORD_RECOVERY_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

export function normalizePortalPath(pathname: string): string {
  if (!pathname) return '/';
  const trimmed = pathname.replace(/\/+$/, '');
  return trimmed || '/';
}

export function isPortalPasswordResetPath(pathname: string): boolean {
  return (PORTAL_PASSWORD_RESET_PATHS as readonly string[]).includes(normalizePortalPath(pathname));
}

export function readPasswordRecoveryFromLocation(loc: LocationParts): boolean {
  const hash = new URLSearchParams(loc.hash.replace(/^#/, ''));
  const search = loc.search.startsWith('?') ? loc.search.slice(1) : loc.search;
  const query = new URLSearchParams(search);
  if (hash.get('type') === 'recovery' || query.get('type') === 'recovery') return true;
  // PKCE: recovery landar som ?code= på portalens inloggningssökväg.
  if (query.get('code') && isPortalPasswordResetPath(loc.pathname)) return true;
  return false;
}

function currentLocation(): LocationParts | null {
  if (typeof window === 'undefined') return null;
  return {
    pathname: window.location.pathname,
    search: window.location.search,
    hash: window.location.hash,
  };
}

/** Måste köras innan Supabase läser URL:en (detectSessionInUrl). */
export function capturePasswordRecoveryFromLocation(loc?: LocationParts): boolean {
  const source = loc ?? currentLocation();
  if (source && readPasswordRecoveryFromLocation(source)) {
    persistPasswordRecoveryPending(true);
  } else if (readPersistedPasswordRecovery()) {
    passwordRecoveryPending = true;
  }
  return passwordRecoveryPending;
}

export function isPasswordRecoveryPending(): boolean {
  return passwordRecoveryPending || readPersistedPasswordRecovery();
}

export function markPasswordRecoveryPending(): void {
  persistPasswordRecoveryPending(true);
}

export function clearPasswordRecoveryPending(): void {
  persistPasswordRecoveryPending(false);
}

export function portalPasswordResetRedirectTo(
  path: PortalPasswordResetPath,
  origin?: string,
): string {
  const base = (origin ?? (typeof window !== 'undefined' ? window.location.origin : '')).replace(
    /\/$/,
    '',
  );
  return `${base}${path}`;
}

export function validateNewPassword(password: string, confirm: string): string | null {
  if (!password || password.length < PASSWORD_RESET_MIN_LENGTH) {
    return `Lösenordet måste vara minst ${PASSWORD_RESET_MIN_LENGTH} tecken.`;
  }
  if (password !== confirm) return 'Lösenorden matchar inte.';
  return null;
}

export async function requestPortalPasswordReset(
  client: PasswordResetAuthClient,
  email: string,
  path: PortalPasswordResetPath,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const trimmed = email.trim();
  if (!trimmed) return { ok: false, error: 'Ange e-postadress.' };

  const { error } = await client.auth.resetPasswordForEmail(trimmed, {
    redirectTo: portalPasswordResetRedirectTo(path),
  });

  if (error) {
    const msg = error.message.toLowerCase();
    if (msg.includes('rate') || msg.includes('limit')) {
      return { ok: false, error: 'För många försök. Vänta en stund och försök igen.' };
    }
    console.error('[password-reset]', error.message);
  }

  return { ok: true };
}

export async function updatePasswordFromRecovery(
  client: PasswordResetAuthClient,
  password: string,
  confirm: string,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const invalid = validateNewPassword(password, confirm);
  if (invalid) return { ok: false, error: invalid };

  const { error } = await client.auth.updateUser({ password });
  if (error) {
    return { ok: false, error: error.message || 'Kunde inte uppdatera lösenordet.' };
  }

  clearPasswordRecoveryPending();
  return { ok: true };
}
